import { formatCnpj } from './cnpj';
import { formatCpf } from './cpf';
import { bufferPointGeoJSON } from './geoBuffer';

function onlyDigits(s) {
  return String(s || '').replace(/\D/g, '');
}

function unwrap(payload) {
  if (!payload) return null;
  const inner = payload.data ?? payload;
  if (inner && typeof inner === 'object' && inner.data && inner.id == null) {
    return inner.data;
  }
  return inner;
}

function clip(s, n) {
  const t = String(s || '').trim();
  return t ? t.slice(0, n) : undefined;
}

function uf2(s) {
  const t = String(s || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(t) ? t : undefined;
}

/** CNPJ (14) ou CPF (11). Aceita string suja do pypdf (CNPJ + coordenadas). */
export function documentDigitsFromParsed(parsed) {
  const raw = parsed?.requerente_cnpj_normalizado || parsed?.requerente_cnpj;
  const asCnpj = formatCnpj(raw);
  if (asCnpj) return onlyDigits(asCnpj);
  const d = onlyDigits(raw);
  return d.length === 11 ? d : '';
}

export function findClientByDocument(clients, docDigits) {
  const want = onlyDigits(docDigits);
  if (!want) return null;
  return (Array.isArray(clients) ? clients : []).find(
    (c) => onlyDigits(c.cnpj || c.document || c.cpf) === want,
  ) || null;
}

export function findClientByCompanyId(clients, companyId) {
  if (!companyId) return null;
  return (Array.isArray(clients) ? clients : []).find(
    (c) => String(c.company_id || '') === String(companyId),
  ) || null;
}

export function findEnterpriseMatch(enterprises, { nome, companyId, cnpj }) {
  const list = Array.isArray(enterprises) ? enterprises : [];
  const digits = onlyDigits(cnpj);
  if (digits) {
    const byCnpj = list.find((e) => onlyDigits(e.cnpj) === digits);
    if (byCnpj) return byCnpj;
  }
  if (companyId) {
    const byCompany = list.find((e) => String(e.company_id || '') === String(companyId));
    if (byCompany) return byCompany;
  }
  const n = String(nome || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (n.length >= 2) {
    const exact = list.find((e) => {
      const en = String(e.name || e.nome || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      return en && (en === n || en.includes(n) || n.includes(en));
    });
    if (exact) return exact;
  }
  return null;
}

async function listCompanies(identityService) {
  try {
    const payload = unwrap(await identityService.getCompanies({ limit: 1000 }));
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
  } catch {
    /* ignore */
  }
  return [];
}

async function listClients(identityService) {
  try {
    const payload = unwrap(await identityService.getClients({ limit: 1000 }));
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
  } catch {
    /* ignore */
  }
  return [];
}

async function findCompanyByCnpj(identityService, cnpj) {
  if (!cnpj) return null;
  try {
    const existing = unwrap(await identityService.getCompanyByCnpj(cnpj));
    if (existing?.id) return existing;
  } catch {
    /* 404 ou path com barra */
  }
  const want = onlyDigits(cnpj);
  const companies = await listCompanies(identityService);
  return companies.find((c) => onlyDigits(c.cnpj) === want) || null;
}

async function findOrCreateCompany(identityService, parsed) {
  const digits = documentDigitsFromParsed(parsed);
  const cnpj = formatCnpj(digits);
  if (!cnpj) return null;
  const existing = await findCompanyByCnpj(identityService, cnpj);
  if (existing?.id) return existing;
  const payload = {
    name: clip(parsed.requerente_nome || `Empresa ${cnpj}`, 255),
    cnpj,
    address: clip(parsed.requerente_endereco || parsed.empreendimento_endereco, 500),
    city: clip(parsed.requerente_cidade || parsed.empreendimento_cidade, 100),
    state: uf2(parsed.requerente_uf || parsed.empreendimento_uf),
  };
  try {
    const created = unwrap(await identityService.createCompany(payload));
    if (created?.id) return created;
  } catch (err) {
    const again = await findCompanyByCnpj(identityService, cnpj);
    if (again?.id) return again;
    throw err;
  }
  return findCompanyByCnpj(identityService, cnpj);
}

async function findOrCreatePerson(identityService, parsed) {
  const digits = documentDigitsFromParsed(parsed);
  const cpf = formatCpf(digits);
  if (!cpf) return null;
  try {
    const existing = unwrap(await identityService.getPersonByCpf(cpf));
    if (existing?.id) return existing;
  } catch {
    /* 404 */
  }
  return unwrap(
    await identityService.createPerson({
      name: clip(parsed.requerente_nome || `Pessoa ${cpf}`, 255),
      cpf,
      city: clip(parsed.empreendimento_cidade, 100),
      state: uf2(parsed.empreendimento_uf),
    }),
  );
}

export async function ensureTipoLicencaFromPdf({ parsed, tipos }) {
  const nome = String(parsed.tipo_licenca_sugerido || '').trim();
  if (!nome) return { id: null, created: false };
  const list = Array.isArray(tipos) ? tipos : [];
  const found = list.find((t) => String(t.nome || t.name || '').trim() === nome);
  return { id: found?.id || null, created: false, nome: found?.nome || found?.name || nome };
}

export async function ensureRequerenteFromPdf({ parsed, clients, identityService }) {
  const digits = documentDigitsFromParsed(parsed);
  const existing = findClientByDocument(clients, digits);
  if (existing) {
    return {
      id: existing.id,
      created: false,
      companyId: existing.company_id || null,
      personId: existing.person_id || null,
    };
  }
  if (digits.length === 14) {
    const company = await findOrCreateCompany(identityService, parsed);
    if (!company?.id) return { id: null, created: false };
    const localByCompany = findClientByCompanyId(clients, company.id);
    if (localByCompany) {
      return { id: localByCompany.id, created: false, companyId: company.id, personId: null };
    }
    const remoteClients = await listClients(identityService);
    const remoteByCompany = findClientByCompanyId(remoteClients, company.id);
    if (remoteByCompany) {
      return { id: remoteByCompany.id, created: false, companyId: company.id, personId: null };
    }
    try {
      const client = unwrap(
        await identityService.createClient({
          client_type: 'legal_entity',
          client_status: 'active',
          company_id: company.id,
        }),
      );
      return { id: client?.id || null, created: Boolean(client?.id), companyId: company.id, personId: null };
    } catch (err) {
      const after = findClientByCompanyId(await listClients(identityService), company.id);
      if (after) {
        return { id: after.id, created: false, companyId: company.id, personId: null };
      }
      throw err;
    }
  }
  if (digits.length === 11) {
    const person = await findOrCreatePerson(identityService, parsed);
    if (!person?.id) return { id: null, created: false };
    const byPerson = (Array.isArray(clients) ? clients : []).find(
      (c) => String(c.person_id || '') === String(person.id),
    );
    if (byPerson) {
      return { id: byPerson.id, created: false, companyId: null, personId: person.id };
    }
    const client = unwrap(
      await identityService.createClient({
        client_type: 'individual',
        client_status: 'active',
        person_id: person.id,
      }),
    );
    return { id: client?.id || null, created: Boolean(client?.id), companyId: null, personId: person.id };
  }
  return { id: null, created: false };
}

export async function ensureEmpreendimentoFromPdf({
  parsed,
  enterprises,
  identityService,
  companyId,
  personId,
}) {
  const nome = clip(parsed.empreendimento_nome, 255);
  const lat = parsed.latitude == null || parsed.latitude === '' ? null : Number(parsed.latitude);
  const lng = parsed.longitude == null || parsed.longitude === '' ? null : Number(parsed.longitude);
  const hasCoords = lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  const existing = findEnterpriseMatch(enterprises, {
    nome,
    companyId,
    cnpj: documentDigitsFromParsed(parsed),
  });
  const geo = {
    address: clip(parsed.empreendimento_endereco, 500),
    city: clip(parsed.empreendimento_cidade, 100),
    state: uf2(parsed.empreendimento_uf),
    latitude: hasCoords ? lat : undefined,
    longitude: hasCoords ? lng : undefined,
    area_geojson: hasCoords ? bufferPointGeoJSON(lat, lng, 50) : undefined,
    cnpj: formatCnpj(documentDigitsFromParsed(parsed)) || undefined,
  };
  if (existing?.id) {
    const missingCoords = existing.latitude == null || existing.longitude == null;
    if (hasCoords && missingCoords) {
      await identityService.updateEnterprise(existing.id, {
        latitude: lat,
        longitude: lng,
        area_geojson: geo.area_geojson,
        address: geo.address,
        city: geo.city,
        state: geo.state,
      });
    }
    return { id: existing.id, created: false, updatedCoords: Boolean(hasCoords && missingCoords) };
  }
  if (!nome || (!companyId && !personId)) {
    return { id: null, created: false };
  }
  const created = unwrap(
    await identityService.createEnterprise({
      name: nome,
      company_id: companyId || undefined,
      owner_person_id: companyId ? undefined : personId || undefined,
      ...geo,
    }),
  );
  return { id: created?.id || null, created: Boolean(created?.id), updatedCoords: false };
}
